export const PROFILE_COPY = {
  LOAD_FAILED: 'Failed to load, Please try again',
  UNAVAILABLE: '-',
  /** What a screen reader says in place of the "-". */
  UNAVAILABLE_SR: 'Unavailable',
  CONTACT_US: 'Contact Us',
  LOGOUT_TITLE: 'Log out?',
  LOGOUT_CONFIRM: 'Logout',
  LOGOUT_BODY:
    "Are you sure you want to log out? You'll need to sign in again to access your account.",
  TICKETS_ACTIVE: (count: number) => `${count} Tickets Active`,
} as const;

/**
 * ADR-0029 — the Profile menu's "Switch to {Account} Account" row asks for confirmation first,
 * mirroring greenway_mobile's `showSwitchAccountConfirmSheet` (the web build had shipped this row
 * wired straight to the mutation with no confirmation step at all, unlike mobile). One button, no
 * Cancel — mobile's sheet is dismissed by swipe/mask-tap only, not a second action.
 */
export const SWITCH_ACCOUNT_COPY = {
  TITLE: (otherAccountLabel: string) => `Switch to ${otherAccountLabel} account?`,
  BODY: (otherAccountLabel: string) =>
    `You're about to switch to your ${otherAccountLabel} Account. Your profile and available features will change accordingly.`,
  CONFIRM: 'Switch Profile',
} as const;

export const CHANGE_PASSWORD_COPY = {
  STEP1_TITLE: 'Change Password',
  STEP1_SUBTITLE: 'Enter your phone number to get a verification code to change your password.',
  STEP3_TITLE: 'Change your password',
  STEP3_SUBTITLE: 'Create a secure password for your Field Pro account',
  MISMATCH: 'Passwords do not match',
  MUST_DIFFER: 'New password must be different from current password',
  SUCCESS: 'Password updated successfully.',
} as const;

/** The shared code-entry step — login's copy (grilling Q11). */
export const CODE_ENTRY_COPY = {
  TITLE: 'Enter the 6 Digit Code',
  SUBTITLE: (last4: string) => `We have sent you a code to the phone number ending with ${last4}`,
  LABEL: 'Verification Code',
  INVALID: 'Please enter valid code',
  RESEND: 'Resend code',
  /** Rendered after the "Resend code" button: "- {N} Attempts remaining". */
  ATTEMPTS: (n: number) => `- ${n} Attempts remaining`,
  COOLDOWN: (seconds: number) =>
    `Resend code in ${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`,
  TOO_MANY: 'Too many attempts. Please try again later',
  EXPIRED: 'Your verification code has expired. Please try again.',
} as const;

export const PROFILE_POLICY = { NAME_MAX: 50, EMAIL_MAX: 320 } as const;

export const EDIT_PROFILE_COPY = {
  TITLE: 'Edit Profile',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  EMAIL: 'Email',
  PHONE: 'Phone Number',
  CHANGE_PHONE: 'Change phone number',
  SAVE: 'Save',
  INVALID_NAME: 'Enter a valid name',
  INVALID_EMAIL: 'Enter a valid email address.',
  EMAIL_TAKEN: 'This email address already exists',
  SAVED: 'Profile updated successfully.',
  SAVE_FAILED: 'Something went wrong. Please try again.',
} as const;

export const PROFILE_ERROR_CODES = {
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  OTP_INVALID: 'OTP_INVALID',
  PASSWORD_MUST_BE_DIFFERENT: 'PASSWORD_MUST_BE_DIFFERENT',
  OTP_EXPIRED: 'OTP_EXPIRED',
  OTP_SESSION_NOT_FOUND: 'OTP_SESSION_NOT_FOUND',
  OTP_SESSION_EXPIRED: 'OTP_SESSION_EXPIRED',
  OTP_VERIFICATION_REQUIRED: 'OTP_VERIFICATION_REQUIRED',
  RESEND_RATE_LIMIT_EXCEEDED: 'RESEND_RATE_LIMIT_EXCEEDED',
  RESEND_COOLDOWN_ACTIVE: 'RESEND_COOLDOWN_ACTIVE',
  PHONE_ALREADY_EXISTS: 'PHONE_ALREADY_EXISTS',
  PHONE_SAME_AS_CURRENT: 'PHONE_SAME_AS_CURRENT',
  PHONE_VERIFY_REQUIRED: 'PHONE_VERIFY_REQUIRED',
  OTP_PHONE_UNDELIVERABLE: 'OTP_PHONE_UNDELIVERABLE',
  INVALID_SESSION: 'INVALID_SESSION',
} as const;

/** The Phone change (US-117543 §C, §E, §G). AC wording wins over the design's punctuation. */
export const PHONE_CHANGE_COPY = {
  STEP1_TITLE: 'Update Phone Number',
  STEP1_SUBTITLE: 'Enter your phone number to get a verification code.',
  STEP3_TITLE: 'Enter New Phone Number',
  STEP3_SUBTITLE: 'Enter your new phone number to keep your account details up to date.',
  PLACEHOLDER: 'Enter Phone',
  INVALID: 'Enter a valid 10-digit US phone number',
  SAME_AS_CURRENT: 'New phone number must be different from the current one',
  TAKEN: 'This number is already in use.',
  SUCCESS: 'Phone number updated successfully.',
} as const;
